import {
  Assets as AssetsIcon,
  Integrations as IntegrationsIcon,
  Seeds as SeedsIcon,
} from '@praetorian-chariot/ui';
import { useEffect, useMemo, useState } from 'react';

import { Loader } from '@/components/Loader';
import { Timeline } from '@/components/Timeline';
import { useGetCapabilities } from '@/hooks/api/useGetCapabilities';
import { Attribute, Preseed } from '@/types';

export const PreseedContextMap = ({
  attribute,
  sourceAttributes,
}: {
  attribute: Attribute;
  sourceAttributes: Attribute[];
}) => {
  const [resolvedContextMap, setResolvedContextMap] = useState<Attribute[]>([]);
  const { capabilities } = useGetCapabilities();

  // Create a lookup map from capability name to title
  const capabilityTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    capabilities.forEach(cap => {
      if (cap.title && cap.title.trim() !== '') {
        map.set(cap.name, cap.title);
      }
    });
    return map;
  }, [capabilities]);

  // Helper function to get human-friendly capability name
  const getCapabilityTitle = (capabilityName?: string) => {
    if (!capabilityName) return 'unknown capability';
    return capabilityTitleMap.get(capabilityName) || capabilityName;
  };

  useEffect(() => {
    setResolvedContextMap([]);
  }, [attribute.key]);

  useEffect(() => {
    // Use the provided sourceAttributes directly since they contain the full path
    setResolvedContextMap([...sourceAttributes].reverse());
  }, [JSON.stringify(sourceAttributes)]);

  const getNodeEdgeLabel = (item: Attribute) => {
    const keyPrefix = item.key.split('#')[1];

    if (keyPrefix === 'asset') {
      if (item.capability === 'seed') {
        return [`added as seed`, item.name ?? ''];
      }
      return [
        `discovered by ${getCapabilityTitle(item.capability)}`,
        `${item.name} (${item.dns})`,
      ];
    }

    if (keyPrefix === 'port') {
      const port = item.value;
      return [
        `discovered by ${getCapabilityTitle(item.capability)}`,
        `${port ? `Port ${port}` : '(unknown port)'}`,
      ];
    }

    if (keyPrefix === 'preseed') {
      const preseed = item as Preseed;

      if (
        preseed.display === 'tlscert' ||
        preseed.capability === 'collect-tls'
      ) {
        return [
          `discovered by ${getCapabilityTitle(preseed.capability)}`,
          `TLS certificate (${preseed.title})`,
          preseed.metadata?.value,
        ];
      }
      return [
        `discovered by ${getCapabilityTitle(preseed.capability)}`,
        `preseed discovered by ${getCapabilityTitle(preseed.capability)}${preseed.metadata?.value ? ` on port ${preseed.metadata?.value}` : ''}`,
      ];
    }

    return ['', ''];
  };

  const resolvedContextMapWithLabel = useMemo(() => {
    return resolvedContextMap.map(item => {
      const [edge, node, metadata] = getNodeEdgeLabel(item);
      return {
        ...item,
        edge,
        node,
        metadata,
      };
    });
  }, [JSON.stringify(resolvedContextMap), capabilityTitleMap]);

  return (
    <Loader
      className="h-[300px] overflow-auto"
      isLoading={resolvedContextMap.length === 0}
    >
      <Timeline
        items={resolvedContextMapWithLabel.map(item => ({
          title: item.node,
          description: (
            <div
              className="text-chariot-text-secondary text-xs"
              data-testid="timeline-description"
            >
              <div>{item.edge}</div>
            </div>
          ),
          icon: (
            <div className="flex items-center justify-center rounded-full border">
              {(item.key.startsWith('#preseed') ||
                item.key.startsWith('#port')) && (
                <div data-testid="integrations-icon">
                  <IntegrationsIcon />
                </div>
              )}
              {item.key.startsWith('#asset') && item.capability !== 'seed' && (
                <div data-testid="assets-icon">
                  <AssetsIcon />
                </div>
              )}
              {item.capability === 'seed' && (
                <div data-testid="seeds-icon">
                  <SeedsIcon />
                </div>
              )}
            </div>
          ),
        }))}
      />
    </Loader>
  );
};
